import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import CloseIcon from 'mdi-react/CloseIcon';
import { Row } from '@prisjakt/ui/list/variants';
import { AppContext } from '@prisjakt/ui-app-context';
import { NudgeDown } from '@prisjakt/ui/hideable/variants';
import { Speed } from '@prisjakt/ui/definitions';
import { ViewportContext } from '../viewport';
import { CoBrandingBannerState } from './reducer';
import * as actions from './actions';
import * as S from './styles';
import { AppState } from '../../reducers';

const DISPLAY_THROTTLING_PERIOD = 24 * 60 * 60 * 1000;
const AUTO_ACKNOWLEDGE_DELAY = 6 * 1000;

const closeIcon = <CloseIcon />;

type State = CoBrandingBannerState & {
  acceptedCookieBannerVersion: string | undefined;
};

const mapStateToProps = (state: AppState): State => ({
  ...state.coBrandingBanner,
  acceptedCookieBannerVersion: state.globals.cookieBanner.version,
});

type Props = ReturnType<typeof mapStateToProps> & typeof actions;

export function CoBrandingBannerImpl({
  isActive,
  lastAcknowledged,
  acceptedCookieBannerVersion,
  acknowledgeCoBrandingBanner,
}: Props) {
  const { market, renderMode, t } = useContext(AppContext);
  const { isMobile } = useContext(ViewportContext);

  const { cookieVersion } =
    market.privacySettings && market.privacySettings[renderMode];

  const isCookieBannerVisible = acceptedCookieBannerVersion !== cookieVersion;

  const shouldBeVisible =
    lastAcknowledged === undefined ||
    lastAcknowledged < Date.now() - DISPLAY_THROTTLING_PERIOD;

  const isVisible =
    market.showSchibstedCoBranding &&
    shouldBeVisible &&
    isActive &&
    !isCookieBannerVisible;

  useEffect(() => {
    const timeoutId = isVisible
      ? setTimeout(acknowledgeCoBrandingBanner, AUTO_ACKNOWLEDGE_DELAY)
      : undefined;
    return () => clearTimeout(timeoutId);
  }, [isVisible, acknowledgeCoBrandingBanner]);

  if (!isActive) {
    return null;
  }

  return (
    <NudgeDown visible={isVisible} Container={S.Banner} speed={Speed.Slow}>
      <Row gap={1} Container={S.ContentsRow}>
        <S.Logo
          alt="Schibsted"
          width="128"
          src={`${market.cdn}/g/rfe/logos/schibsted/schibsted_dust_black.png`}
        />
        <S.Text>
          <span>
            {`${
              isMobile
                ? ''
                : t('tracking-settings', 'co-branding.part-of', {
                    companyName: t('meta', 'company.name'),
                  })
            } ${t('tracking-settings', 'co-branding.data-controller')} `}
            <S.Link to="/privacy-settings">
              {t('tracking-settings', 'co-branding.read-more')}
            </S.Link>
          </span>
        </S.Text>
        <S.CloseButton
          variant="icon"
          tabIndex={0}
          icon={closeIcon}
          onClick={acknowledgeCoBrandingBanner}
        />
      </Row>
    </NudgeDown>
  );
}

export const CoBrandingBanner = connect(
  mapStateToProps,
  actions
)(CoBrandingBannerImpl);
